import React, { useRef, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { Brush, Evaluator, ADDITION, SUBTRACTION, INTERSECTION } from 'three-bvh-csg';

const Three = {
  Mesh: THREE.Mesh,
  Group: THREE.Group,
  Scene: THREE.Scene,
  PerspectiveCamera: THREE.PerspectiveCamera,
  WebGLRenderer: THREE.WebGLRenderer,
  BoxGeometry: THREE.BoxGeometry,
  SphereGeometry: THREE.SphereGeometry,
  CylinderGeometry: THREE.CylinderGeometry,
  ConeGeometry: THREE.ConeGeometry,
  TorusGeometry: THREE.TorusGeometry,
  TorusKnotGeometry: THREE.TorusKnotGeometry,
  PlaneGeometry: THREE.PlaneGeometry,
  CircleGeometry: THREE.CircleGeometry,
  ExtrudeGeometry: THREE.ExtrudeGeometry,
  Shape: THREE.Shape,
  ShapeGeometry: THREE.ShapeGeometry,
  Path: THREE.Path,
  TubeGeometry: THREE.TubeGeometry,
  PolyhedronGeometry: THREE.PolyhedronGeometry,
  LatheGeometry: THREE.LatheGeometry,
  EdgesGeometry: THREE.EdgesGeometry,
  MeshBasicMaterial: THREE.MeshBasicMaterial,
  MeshStandardMaterial: THREE.MeshStandardMaterial,
  MeshLambertMaterial: THREE.MeshLambertMaterial,
  MeshPhongMaterial: THREE.MeshPhongMaterial,
  MeshToonMaterial: THREE.MeshToonMaterial,
  MeshPhysicalMaterial: THREE.MeshPhysicalMaterial,
  MeshNormalMaterial: THREE.MeshNormalMaterial,
  ShadowMaterial: THREE.ShadowMaterial,
  Color: THREE.Color,
  Vector2: THREE.Vector2,
  Vector3: THREE.Vector3,
  Vector4: THREE.Vector4,
  Euler: THREE.Euler,
  Quaternion: THREE.Quaternion,
  Matrix4: THREE.Matrix4,
  Raycaster: THREE.Raycaster,
  BufferGeometryUtils,
  Curve: THREE.Curve,
  CurvePath: THREE.CurvePath,
  CatmullRomCurve3: THREE.CatmullRomCurve3,
  AxesHelper: THREE.AxesHelper,
  GridHelper: THREE.GridHelper,
  Brush,
  Evaluator,
  SUBTRACTION,
  ADDITION,
  INTERSECTION,
};

export const DynamicMesh = forwardRef(({ geometryCode, onMeshConfigsGenerated }, ref) => {
    const groupRef = useRef();

    useImperativeHandle(ref, () => ({
        getGroup: () => groupRef.current
    }));

    const meshConfigs = useMemo(() => {
        try {
            const meshFactory = new Function(
                ...Object.keys(Three),
                `return (() => { ${geometryCode} })()`
            );

            const result = meshFactory(...Object.values(Three));

            if (!Array.isArray(result)) {
                throw new Error('Geometry code must return an array of mesh configurations');
            }

            return result.map(config => ({
                geometry: config.geometry,
                color: config.color || '#ffffff',
                roughness: config.roughness ?? 0.5,
                metalness: config.metalness ?? 0.5,
                clearcoat: config.clearcoat ?? 0,
                position: config.position || [0, 0, 0],
                rotation: config.rotation || [0, 0, 0],
                scale: config.scale || [1, 1, 1]
            }));
        } catch (error) {
            console.error('Error processing geometry code:', error);
            return [];
        }
    }, [geometryCode]);

    useEffect(() => {
        if (typeof onMeshConfigsGenerated === 'function') {
            onMeshConfigsGenerated(meshConfigs.length > 0);
        }
    }, [meshConfigs, onMeshConfigsGenerated]);

    useFrame((_, delta) => {
        if (groupRef.current) {
            groupRef.current.rotation.y += delta * 0.5;
        }
    });

    return (
        <group ref={groupRef}>
            {meshConfigs.map((config, index) => (
                <MeshObject key={index} config={config} />
            ))}
        </group>
    );
});

function MeshObject({ config }) {
    return (
        <mesh
            geometry={config.geometry}
            position={config.position}
            rotation={config.rotation}
            scale={config.scale}
            castShadow
            receiveShadow
        >
            <meshPhysicalMaterial
                color={config.color}
                roughness={config.roughness}
                metalness={config.metalness}
                clearcoat={config.clearcoat}
            />
        </mesh>
    );
}
